<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">AR & TD</h1>
        <p class="pg-title-sub">Review runner activity rank by total distance or completed sessions</p>
      </div>
    </div>

    <div class="artd-card artd-filter-card">
      <div class="artd-card-header">
        <span class="artd-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 6h18"/>
            <path d="M7 12h10"/>
            <path d="M10 18h4"/>
          </svg>
        </span>
        <div class="artd-card-title-wrap">
          <h2 class="artd-card-title">Report Filters</h2>
          <p class="artd-card-subtitle">Choose a date window and ranking metric for the leaderboard</p>
        </div>
      </div>

      <div class="artd-filter-grid">
        <div class="artd-filter-field">
          <label class="artd-label">Date range</label>
          <date-range-field v-model="rangeData"></date-range-field>
        </div>
        <div class="artd-filter-field artd-sort-field">
          <label class="artd-label" for="artd-sort-by">Sort by</label>
          <div class="artd-select-wrap">
            <select id="artd-sort-by" v-model="sortBy" name="sortBy" @change="sortByChangeHandle" class="artd-select">
              <option value="Distance">Total Distance</option>
              <option value="Sessions"># of Sessions</option>
            </select>
            <svg class="artd-select-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="artd-card artd-table-card">
      <div class="artd-card-header artd-card-header--table">
        <span class="artd-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 6h13"/>
            <path d="M8 12h13"/>
            <path d="M8 18h13"/>
            <path d="M3 6h.01"/>
            <path d="M3 12h.01"/>
            <path d="M3 18h.01"/>
          </svg>
        </span>
        <div class="artd-card-title-wrap">
          <h2 class="artd-card-title">Runner Leaderboard</h2>
          <p class="artd-card-subtitle">Ranked totals for the selected period</p>
        </div>
        <span class="artd-total-badge">{{ pageTotal || 0 }} runners</span>
      </div>

      <div v-if="loading" class="artd-table-skeleton">
        <div class="artd-sk-header">
          <div v-for="n in 6" :key="'artd-sk-head-' + n" class="artd-sk-cell sk-shimmer"></div>
        </div>
        <div v-for="row in 8" :key="'artd-sk-row-' + row" class="artd-sk-row">
          <div v-for="col in 6" :key="'artd-sk-col-' + row + '-' + col" class="artd-sk-cell sk-shimmer"></div>
        </div>
      </div>

      <div v-else class="artd-table-wrap">
        <table v-if="tableData && tableData.length" class="artd-data-table">
          <thead class="artd-table-head">
            <tr class="artd-table-row">
              <th class="artd-table-cell artd-table-cell--rank">Rank</th>
              <th class="artd-table-cell artd-table-cell--runner">Runner Name</th>
              <th class="artd-table-cell artd-table-cell--id">Id</th>
              <th class="artd-table-cell artd-table-cell--gender">Sex</th>
              <th class="artd-table-cell artd-table-cell--sessions"># of Sessions</th>
              <th class="artd-table-cell artd-table-cell--distance">Distance(KM)</th>
            </tr>
          </thead>
          <tbody class="artd-table-body">
            <tr v-for="(row, rowIndex) in tableData" :key="row.id || rowIndex" class="artd-table-row">
              <td class="artd-table-cell artd-table-cell--rank">{{ (pageIndex * pageSize) + rowIndex + 1 }}</td>
              <td class="artd-table-cell artd-table-cell--runner">{{ row.runnerName }}</td>
              <td class="artd-table-cell artd-table-cell--id">
                <a href="" class="artd-runner-link" @click.stop.prevent="openRunnerData(row)">{{ row.id }}</a>
              </td>
              <td class="artd-table-cell artd-table-cell--gender">{{ row.gender }}</td>
              <td class="artd-table-cell artd-table-cell--sessions">{{ row.Sessions }}</td>
              <td class="artd-table-cell artd-table-cell--distance">{{ row.Distance }}</td>
            </tr>
          </tbody>
          <tfoot v-if="footer && footer.length" class="artd-table-foot">
            <tr class="artd-table-row">
              <td class="artd-table-cell artd-table-cell--rank">Total</td>
              <td class="artd-table-cell artd-table-cell--runner"></td>
              <td class="artd-table-cell artd-table-cell--id"></td>
              <td class="artd-table-cell artd-table-cell--gender"></td>
              <td class="artd-table-cell artd-table-cell--sessions">{{ footer[0][4] }}</td>
              <td class="artd-table-cell artd-table-cell--distance">{{ footer[0][5] }}</td>
            </tr>
          </tfoot>
        </table>

        <div v-if="tableData && tableData.length === 0" class="artd-empty">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
            <path d="M8 11h6"/>
          </svg>
          <p class="artd-empty-text">No runner totals found for this range.</p>
        </div>

        <div class="artd-pagination">
          <v-pagination @page-change="pageChange" @page-size-change="pageSizeChange" :total="pageTotal" :page-size="pageSize" :layout="['total', 'prev', 'pager', 'next', 'sizer', 'jumper']"></v-pagination>
        </div>
      </div>
    </div>

  </container>
</template>
<script>
import Container from "@/components/Container"
import { VPagination } from 'vue-easytable'
import DateRangeField from '@/components/Forms/DateRangeField'
import axios from 'axios'
import moment from 'moment'
export default {
  name: "tp",
  data() {
    return {

      tableData :[],
      footer:[[]],
      pageIndex:0,
      pageTotal:null,
      pageSize:10,       
      rangeData:{
        startDate: moment().startOf('month').startOf('day').toISOString(),
        endDate:  moment().endOf('month').endOf('day').toISOString(),
      },
      loading:false,
      sortBy:'Distance'
            
    }
  },
  computed: {
  },
  methods: {
    intialize(){
      this.getTotalDistance(this.rangeData.startDate,this.rangeData.endDate,this.pageIndex,this.pageSize,this.sortBy)   
    },
    getTotalDistance(startDate,endDate,pageIndex,pageSize,sortBy){
    //add one day more to get acurate filter
    //var newEndate = moment(endDate, "YYYY-MM-DD").add('days', 1).format('YYYY-MM-DD')
    this.loading=true
    const AuthStr = 'Bearer '.concat(window.localStorage.getItem('default-auth-token'))
    //axios.get(`http://localhost:3007/getTotalDistance?startDate=`+startDate+`&endDate=` + endDate +`&pageIndex=`+pageIndex+`&pageSize=`+pageSize+`&orderBy=`+sortBy+`&staging=`+this.$isStage, { headers: { Authorization: AuthStr } })
    axios.get(this.$expressApi+`getTotalDistance?startDate=`+startDate+`&endDate=` + endDate +`&pageIndex=`+pageIndex+`&pageSize=`+pageSize+`&orderBy=`+sortBy+`&staging=`+this.$isStage, { headers: { Authorization: AuthStr } })
            .then(response => {  
              console.log("resp----",response.data)
              this.tableData=response.data.content
              this.pageTotal = response.data.totalElements
              if(this.tableData.length>0){
                this.footer=[['Total','','','',response.data.totalSessions,response.data.totalDistance]]
              }else{
                this.footer=[]
              }
              this.loading=false
          },error=>{
              this.tableData=[],
              this.footer=[]
              console.log("error",error)
              this.loading=false
          })
    },
    pageChange(pageIndex){
      this.pageIndex = pageIndex-1
      this.getTotalDistance(this.rangeData.startDate,this.rangeData.endDate,this.pageIndex,this.pageSize,this.sortBy)        
    },
     pageSizeChange(pageSize){
      this.pageIndex = 0
      this.pageSize = pageSize
      this.getTotalDistance(this.rangeData.startDate,this.rangeData.endDate,this.pageIndex, this.pageSize,this.sortBy)        
    },  
    sortByChangeHandle(evt){
      this.getTotalDistance(this.rangeData.startDate,this.rangeData.endDate,this.pageIndex, this.pageSize,this.sortBy)        
    },
    openRunnerData(rowData){
      let route = this.$router.resolve({
            name: 'runner-data',
            params: {
                  runnerId: rowData.id
                }
            })
      window.open(route.href, '_blank')
    }  
  },
  created() {
    this.intialize()
  },
  watch: {
     rangeData(oldVal, newVal) {
            this.pageIndex = 0
            this.pageSize= 10
            this.intialize()
            //console.log(oldVal,"---newVal---",newVal)            
            //console.log(this.rangeData.startDate,this.rangeData.endDate,this.pageIndex,this.pageSize)
        }
  },
  components: {
    Container,
    DateRangeField,
    VPagination
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.artd-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.artd-filter-card {
  margin-bottom: 1rem;
}

.artd-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.artd-card-header--table {
  justify-content: space-between;
}

.artd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
}

.artd-card-title-wrap {
  min-width: 0;
}

.artd-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.25;
}

.artd-card-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
  line-height: 1.4;
}

.artd-filter-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(180px, 240px);
  gap: 1rem;
  align-items: end;
}

.artd-filter-field {
  min-width: 0;
}

.artd-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.artd-select-wrap {
  position: relative;
}

.artd-select {
  appearance: none;
  width: 100%;
  height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  padding: 0 34px 0 12px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.artd-select:focus {
  border-color: #5b4fcf;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.artd-select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8a8a9a;
  pointer-events: none;
}

.artd-total-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  margin-left: auto;
  white-space: nowrap;
}

.artd-table-wrap {
  overflow-x: auto;
}

.artd-data-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
}

.artd-table-head {
  background: #413b94;
  color: #fff;
}

.artd-table-cell {
  height: 38px;
  border: 1px solid #e8e8ed;
  color: #1a1a2e;
  font-size: 12px;
  line-height: 1.35;
  padding: 0 10px;
  text-align: center;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.artd-table-head .artd-table-cell {
  color: #fff;
  font-weight: 700;
  height: 34px;
  border-color: rgba(255, 255, 255, 0.18);
}

.artd-table-body .artd-table-row:hover {
  background: #fafafa;
}

.artd-table-foot .artd-table-cell {
  background: #fafafa;
  font-weight: 700;
}

.artd-table-cell--rank {
  width: 8%;
  font-weight: 700;
}

.artd-table-cell--runner {
  width: 36%;
}

.artd-table-cell--id {
  width: 12%;
}

.artd-table-cell--gender {
  width: 9%;
}

.artd-table-cell--sessions {
  width: 17%;
}

.artd-table-cell--distance {
  width: 18%;
  font-weight: 700;
}

.artd-runner-link {
  color: #5b4fcf;
  font-weight: 700;
  text-decoration: none;
}

.artd-runner-link:hover {
  color: #4338ca;
  text-decoration: underline;
}

.artd-table-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.artd-sk-header,
.artd-sk-row {
  display: grid;
  grid-template-columns: 0.7fr 1.5fr 0.9fr 0.7fr 1.1fr 1.1fr;
  gap: 12px;
  align-items: center;
}

.artd-sk-header {
  padding-bottom: 6px;
}

.artd-sk-cell {
  height: 18px;
}

.artd-sk-header .artd-sk-cell {
  height: 24px;
}

.artd-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  min-height: 140px;
  color: #8a8a9a;
  font-size: 13px;
}

.artd-empty-text {
  margin: 0;
}

.artd-pagination {
  padding-top: 1.25rem;
  margin: 0 0 0.25rem;
  font-weight: 700;
}

.daterangepicker.show-calendar .ranges {
  float: left;
}

@media (max-width: 760px) {
  .artd-card {
    padding: 1.25rem;
  }

  .artd-filter-grid {
    grid-template-columns: 1fr;
  }

  .artd-card-header--table {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .artd-total-badge {
    margin-left: 48px;
  }

  .artd-sk-header,
  .artd-sk-row {
    grid-template-columns: 0.8fr 1.2fr 1fr;
  }

  .artd-sk-cell:nth-child(n + 4) {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// AR&TD.vue?264882e8